import { useState } from "react";
import { Layout } from "@/components/Layout";
import api, { formatApiError, formatDate, formatMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { CheckCircle, Warning, CreditCard } from "@phosphor-icons/react";

export default function Account() {
  const { user, refresh, isMember } = useAuth();
  const [loading, setLoading] = useState(false);

  const cancelRenewal = async () => {
    if (!confirm("Cancel auto-renewal? Your access remains until expiration.")) return;
    setLoading(true);
    try {
      await api.post("/membership/cancel-renewal");
      toast.success("Auto-renewal cancelled");
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  };

  const enableRenewal = async () => {
    setLoading(true);
    try {
      await api.post("/membership/enable-renewal");
      toast.success("Auto-renewal enabled");
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  };

  const startCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 lg:px-8 py-10">
        <div className="section-label mb-3">— ACCOUNT</div>
        <h1 className="font-chivo font-black text-4xl lg:text-5xl text-white uppercase tracking-tight mb-8" data-testid="account-headline">Profile & Membership</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="surface p-6" data-testid="profile-card">
            <div className="section-label mb-4">PROFILE</div>
            <dl className="space-y-3 text-sm">
              <Row label="Name" value={user.name} />
              <Row label="Email" value={user.email} />
              <Row label="Company" value={user.company || "—"} />
              <Row label="Phone" value={user.phone || "—"} />
              <Row label="Role" value={user.role} />
              <Row label="Joined" value={formatDate(user.created_at)} />
            </dl>
          </div>

          <div className="surface p-6" data-testid="membership-card">
            <div className="section-label mb-4">MEMBERSHIP</div>
            <div className="flex items-center gap-3 mb-4">
              {isMember ? (
                <>
                  <CheckCircle size={28} weight="fill" className="text-[#00D4FF]" />
                  <span className="font-chivo font-black text-2xl text-white uppercase">Active</span>
                </>
              ) : (
                <>
                  <Warning size={28} weight="fill" className="text-[#FF5722]" />
                  <span className="font-chivo font-black text-2xl text-[#FF5722] uppercase">Inactive</span>
                </>
              )}
            </div>

            <dl className="space-y-3 text-sm border-t border-[#262626] pt-4">
              <Row label="Plan" value={`Annual — ${formatMoney(399)}`} />
              <Row label="Status" value={user.membership_status || "inactive"} />
              <Row label="Renews / Expires" value={user.membership_expires_at ? formatDate(user.membership_expires_at) : "—"} />
              <Row label="Auto-Renew" value={user.auto_renew ? "Enabled" : "Disabled"} />
            </dl>

            <div className="mt-6 flex flex-col gap-2">
              {!isMember && (
                <button onClick={startCheckout} disabled={loading} className="btn-primary w-full" data-testid="checkout-btn">
                  <CreditCard size={16} weight="bold" /> {loading ? "Loading..." : "Activate — $399/yr"}
                </button>
              )}
              {isMember && user.auto_renew && (
                <button onClick={cancelRenewal} disabled={loading} className="btn-secondary w-full" data-testid="cancel-renewal-btn">
                  Cancel Auto-Renewal
                </button>
              )}
              {isMember && !user.auto_renew && (
                <button onClick={enableRenewal} disabled={loading} className="btn-primary w-full" data-testid="enable-renewal-btn">
                  Enable Auto-Renewal
                </button>
              )}
            </div>
            <p className="font-mono text-[10px] text-[#52525B] mt-4 leading-relaxed">
              Members may cancel renewal at any time. Membership remains active until the end of the paid term.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="font-mono text-xs text-[#52525B] tracking-widest uppercase shrink-0">{label}</dt>
      <dd className="font-plex text-sm text-white text-right break-all">{value}</dd>
    </div>
  );
}
